/**
 * Video Editor — Surface 2: brief + clip intake.
 *
 * Phase 4: the brief is conversational — `ConversationalBrief` (the agent-
 * substrate co-pilot) replaces the plain-text brief. Clip upload + the brand
 * and format pickers remain.
 */
import React, { useState, useRef } from 'react';
import type { VideoProject, VideoBrand, VideoAspect, VideoPlatform } from '../types';
import type { VideoEditorApi } from '../videoEditorApi';
import { SurfaceHeader, formatDuration, formatBytes } from './parts';
import ConversationalBrief from './ConversationalBrief';

const BRANDS: { key: VideoBrand; label: string }[] = [
  { key: 'clinical', label: 'Cellcolabs Clinical' },
  { key: 'ab', label: 'Cellcolabs AB' },
  { key: 'asia', label: 'Cellcolabs Asia' },
];
const FORMATS: { aspect: VideoAspect; platform: VideoPlatform; label: string }[] = [
  { aspect: '9:16', platform: 'reels', label: 'Reels · 9:16' },
  { aspect: '1:1', platform: 'feed', label: 'Feed · 1:1' },
  { aspect: '16:9', platform: 'youtube', label: 'YouTube · 16:9' },
  { aspect: '9:16', platform: 'story', label: 'Story · 9:16' },
];

interface Props {
  project: VideoProject;
  api: VideoEditorApi;
  onRefresh: () => Promise<any>;
  // Starts analysis on a fresh project; once analysis exists it just advances
  // to the cut (ProjectWorkspace decides — the brief stays editable here at
  // any stage, reached via the surface stepper).
  onStartAnalysis: () => void;
}

// The brief is a draft object the co-pilot fills (or a legacy string).
function briefFieldCount(b: any): number {
  if (typeof b === 'string') return b.trim() ? 1 : 0;
  if (b && typeof b === 'object' && !Array.isArray(b)) {
    return Object.values(b).filter(v => v != null && v !== '').length;
  }
  return 0;
}

const BriefSurface: React.FC<Props> = ({ project, api, onRefresh, onStartAnalysis }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const clips = project.clips || [];
  const canStart = clips.length > 0 && briefFieldCount(project.brief) > 0;
  // Once analysed, this surface is an editing stop, not the launch point —
  // the primary button advances rather than re-running analysis.
  const analyzed = !!project.analysis;

  async function save(patch: Partial<VideoProject>, tag: string) {
    try {
      setBusy(tag); setErr(null);
      await api.updateProject(project._id, patch);
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Save failed');
    } finally { setBusy(null); }
  }

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    try {
      setBusy('upload'); setErr(null); setUploadPct(0);
      await api.uploadClips(project._id, Array.from(files), setUploadPct);
      await onRefresh();
    } catch (e: any) {
      setErr(e.response?.data?.error || e.message || 'Upload failed');
    } finally { setBusy(null); setUploadPct(null); }
  }

  return (
    <div>
      <SurfaceHeader
        eyebrow="Step 1 of 3"
        title={project.title}
        subtitle="Drop in raw footage and describe what you want. The AI then analyses it and cuts a first pass — you review it on the storyboard."
      />

      {err && <div className="ve-banner ve-banner--error">{err}</div>}

      <div className="ve-split">
        {/* Clips */}
        <div className="ve-panel">
          <h3>Footage <span className="ve-muted">({clips.length})</span></h3>
          <div
            className="ve-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
          >
            <input
              ref={fileRef} type="file" accept="video/*" multiple hidden
              onChange={(e) => onFiles(e.target.files)}
            />
            {busy === 'upload'
              ? <span>Uploading… {uploadPct ?? 0}%</span>
              : <span>Drop clips here, or click to choose files</span>}
          </div>
          {clips.length === 0 && (
            <p className="ve-muted">No clips yet — the dropzone is the place to start.</p>
          )}
          {clips.map((c, i) => (
            <div className="ve-cliprow" key={c._id || i}>
              <span className="ve-cliprow__name">🎞 {c.filename}</span>
              <span className="ve-muted">
                {c.aspect || ''} {c.durationMs ? `· ${formatDuration(c.durationMs / 1000)}` : ''}
                {c.sizeBytes ? ` · ${formatBytes(c.sizeBytes)}` : ''}
              </span>
            </div>
          ))}
        </div>

        {/* Brief + settings */}
        <div className="ve-panel">
          <ConversationalBrief project={project} api={api} onRefresh={onRefresh} />

          <h3>Output brand</h3>
          <div className="ve-chips">
            {BRANDS.map(b => (
              <button
                key={b.key}
                className={`ve-chip${project.brand === b.key ? ' ve-chip--active' : ''}`}
                disabled={!!busy}
                onClick={() => save({ brand: b.key }, 'brand')}
              >{b.label}</button>
            ))}
          </div>

          <h3>Format</h3>
          <div className="ve-chips">
            {FORMATS.map(f => (
              <button
                key={f.label}
                className={`ve-chip${project.format?.platform === f.platform ? ' ve-chip--active' : ''}`}
                disabled={!!busy}
                onClick={() => save({ format: { aspect: f.aspect, platform: f.platform } }, 'format')}
              >{f.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="ve-startbar">
        <span className="ve-muted">
          {analyzed
            ? 'Brief edits save as you make them. Use the steps above to move around.'
            : canStart
              ? 'Ready — the AI will analyse your footage and cut a first pass.'
              : 'Add at least one clip and a brief to start.'}
        </span>
        <button
          className="ve-btn ve-btn--primary"
          disabled={(!analyzed && !canStart) || !!busy}
          onClick={onStartAnalysis}
        >{analyzed ? 'Continue to the storyboard →' : 'Build the cut →'}</button>
      </div>
    </div>
  );
};

export default BriefSurface;
